<script lang="ts" setup>
import { computed } from 'vue'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()

const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => {
  return templateProducts.value && templateProducts.value.length > 0
})

const isLoggedIn = computed(() => userStore.isLogin())

const displayName = computed(() => {
  const state = userStore.$state
  return state.thirdNickName || state.email || state.memberName || props.config.texts.guestName || 'Guest'
})

const subLine = computed(() => {
  const state = userStore.$state
  if (!isLoggedIn.value)
    return props.config.texts.guestTip || ''
  return state.email ? `${state.email} · UID ${state.code}` : `UID ${state.code}`
})

const balanceText = computed(() => (userStore.$state.coinBalance ?? 0).toLocaleString('en-US'))

const vipTitle = computed(() => props.config.texts.vipTitle || 'Novelyn VIP')

// 定义样式变量供 CSS v-bind 使用
const avatarBorderColor = props.config.theme.avatarBorder

function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}
</script>

<template>
  <div class="novelyn-member-page">
    <!-- 头部 -->
    <div class="novelyn-m-head">
      <span class="novelyn-m-circle novelyn-m-circle--tr" />
      <span class="novelyn-m-circle novelyn-m-circle--bl" />
      <div class="m-user">
        <div class="m-avatar">
          <img v-if="userStore.$state.headImageUrl" :src="userStore.$state.thirdHeadImgUrl" alt="avatar">
          <img v-else :src="config.resources.userDefaultAvatar" alt="avatar">
        </div>
        <div class="m-info">
          <div class="m-name">
            <span class="m-name-text">{{ displayName }}</span>
            <span v-if="userStore.$state.subscribe" class="m-vip"><img src="/images/novelyn/crown.svg" alt=""> VIP</span>
          </div>
          <div class="m-uid">
            {{ subLine }}
          </div>
        </div>
        <a v-if="config.showLoginBtn !== false && !isLoggedIn" class="m-signin" @click="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>

    <!-- 钱包卡 -->
    <div class="novelyn-wallet-card bg-n-bg-card">
      <div class="w-chip">
        <img src="/images/novelyn/coin.svg" class="w-22px h-22px" alt="coins">
      </div>
      <div class="w-info">
        <div class="w-label">
          {{ config.texts.myWallet }}
        </div>
        <div class="w-num">
          {{ balanceText }}
        </div>
      </div>
      <router-link v-if="!loading" :to="topupShow ? { name: 'pay' } : { name: 'welfare' }">
        <div class="w-topup" @click="elementClick('TopUp')">
          {{ topupShow ? config.texts.topUp : config.texts.get }}
        </div>
      </router-link>
    </div>

    <!-- VIP 卡 -->
    <component
      :is="userStore.$state.subscribe ? 'div' : 'router-link'"
      v-if="subscribeProducts?.length || userStore.$state.subscribe"
      :to="userStore.$state.subscribe ? undefined : { name: 'subscribe' }"
      class="novelyn-vip-link"
    >
      <div class="novelyn-vip-card">
        <span v-if="!userStore.$state.subscribe" class="novelyn-vip-glow" />
        <div class="v-crown">
          <img src="/images/novelyn/crown.svg" alt="VIP">
        </div>
        <div class="v-info">
          <div class="v-title">
            {{ vipTitle }}
          </div>
          <div v-if="!userStore.$state.subscribe" class="v-sub">
            {{ config.texts.moreCoinsMoreBenefits }}
          </div>
          <div v-else-if="userStore.$state.subscribeEndDate" class="v-sub">
            {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
          </div>
        </div>
        <span v-if="!userStore.$state.subscribe" class="v-cta-pill" @click.stop="elementClick('Details')">
          {{ config.texts.details }}
        </span>
      </div>
    </component>

    <!-- 菜单 -->
    <div class="novelyn-member-menu bg-n-bg-card">
      <router-link :to="{ name: 'recharge' }" class="menu-row">
        <img class="mi" src="/images/novelyn/profile/tw_ic_mine_transaction_history.png" alt="">
        <span class="mi-label">{{ config.texts.transactionHistory }}</span>
        <span class="mi-arrow">›</span>
      </router-link>
      <router-link :to="{ name: 'consume' }" class="menu-row has-line">
        <img class="mi" src="/images/novelyn/profile/tw_ic_mine_chapters_unlocked.png" alt="">
        <span class="mi-label">{{ config.texts.chaptersUnlocked }}</span>
        <span class="mi-arrow">›</span>
      </router-link>
      <router-link :to="{ name: 'contact-us' }" class="menu-row has-line" @click="elementClick('contact_us')">
        <img class="mi" src="/images/novelyn/profile/tw_ic_mine_contactus.png" alt="">
        <span class="mi-label">{{ config.texts.contactUs }}</span>
        <span class="mi-arrow">›</span>
      </router-link>
      <router-link :to="{ name: 'setting' }" class="menu-row has-line" @click="elementClick('setting')">
        <img class="mi" src="/images/novelyn/profile/tw_ic_mine_setting.png" alt="">
        <span class="mi-label">{{ config.texts.setting }}</span>
        <span class="mi-arrow">›</span>
      </router-link>
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style lang="scss" scoped>
.novelyn-member-page {
  min-height: 100dvh;
  padding-bottom: 94px;
  width: 100%;
  background-color: var(--novelyn-bg);
}

/* 头部 */
.novelyn-m-head {
  position: relative;
  padding: 10px 20px 60px;
  overflow: hidden;
  background: var(--novelyn-gradient);
}

.novelyn-m-circle {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;

  &--tr {
    right: -50px;
    top: -60px;
    width: 220px;
    height: 220px;
    background: rgba(255, 255, 255, .16);
  }

  &--bl {
    left: -70px;
    bottom: -110px;
    width: 200px;
    height: 200px;
    background: rgba(255, 255, 255, .1);
  }
}

.m-user {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 14px;
}

.m-avatar {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 62px;
  height: 62px;
  overflow: hidden;
  border: 3px solid v-bind(avatarBorderColor);
  border-radius: 50%;
  background: var(--novelyn-lavender-gradient);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.m-info {
  flex: 1;
  min-width: 0;
}

.m-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 17px;
  font-weight: 900;
  color: #fff;
}

.m-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-vip {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  font-size: 9px;
  font-weight: 900;
  color: #5C430A;
  border-radius: 999px;
  background: var(--novelyn-gold-gradient);

  img {
    width: 10px;
    height: auto;
  }
}

.m-uid {
  overflow: hidden;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, .85);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-signin {
  flex-shrink: 0;
  padding: 9px 18px;
  font-size: 12px;
  font-weight: 900;
  color: var(--novelyn-rose-deep);
  border-radius: 999px;
  background: #fff;
}

/* 钱包卡（上叠头部） */
.novelyn-wallet-card {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  margin: -42px 16px 0;
  padding: 16px 18px;
  border-radius: 20px;
  box-shadow: var(--novelyn-shadow-sm);
}

.w-chip {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  background: var(--novelyn-gold-soft);
}

.w-info {
  flex: 1;
  min-width: 0;
  margin-left: 12px;
}

.w-label {
  font-size: 11px;
  font-weight: 800;
  color: var(--novelyn-ink-2);
}

.w-num {
  font-size: 20px;
  font-weight: 900;
  color: var(--novelyn-ink);
}

.w-topup,
.v-cta-pill {
  position: relative;
  z-index: 2;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 86px;
  height: 38px;
  padding: 0;
  font-size: 13px;
  white-space: nowrap;
}

.w-topup {
  font-weight: 800;
  color: #fff;
  border-radius: 999px;
  background: var(--novelyn-gradient);
  box-shadow: 0 8px 18px rgba(224, 90, 126, .32);
}

/* VIP 卡 */
.novelyn-vip-link {
  display: block;
  margin: 14px 16px 0;
  color: inherit;
  text-decoration: none;
}

.novelyn-vip-card {
  position: relative;
  display: flex;
  align-items: center;
  height: 74px;
  padding: 16px 18px;
  overflow: hidden;
  color: #fff;
  border-radius: 20px;
  background: linear-gradient(120deg, #3A2A33, #5C3A4A);
  box-shadow: 0 12px 26px rgba(58, 42, 51, .3);
}

.novelyn-vip-glow {
  position: absolute;
  z-index: 0;
  top: -40px;
  right: -30px;
  width: 140px;
  height: 140px;
  pointer-events: none;
  border-radius: 50%;
  background: rgba(239, 206, 122, .15);
}

.v-crown {
  position: relative;
  z-index: 2;
  margin-right: 12px;

  img {
    display: block;
    width: 22px;
    height: auto;
  }
}

.v-info {
  position: relative;
  z-index: 2;
  flex: 1;
  min-width: 0;
}

.v-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 14px;
  font-weight: 900;
  color: #EFCE7A;
}

.v-sub {
  overflow: hidden;
  margin-top: 2px;
  font-size: 11px;
  color: rgba(255, 255, 255, .8);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-cta-pill {
  font-weight: 900;
  color: #5C430A;
  border-radius: 999px;
  background: var(--novelyn-gold-gradient);
  box-shadow: 0 8px 18px rgba(201, 162, 39, .35);
}

/* 菜单 */
.novelyn-member-menu {
  margin: 14px 16px 0;
  padding: 4px 16px;
  border-radius: 20px;
  box-shadow: var(--novelyn-shadow-sm);
}

.menu-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 4px;
  font-size: 14px;
  font-weight: 800;
  color: var(--novelyn-ink);

  &.has-line {
    border-top: 1px solid var(--novelyn-line);
  }

  .mi {
    display: block;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 8px;
    object-fit: contain;
  }

  .mi-label {
    flex: 1;
    min-width: 0;
  }

  .mi-arrow {
    margin-left: auto;
    color: var(--novelyn-ink-3);
  }
}
</style>
